import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { llamarGoogleCalendar, avisarEstadoGoogleCalendar } from '../utils/googleCalendar';

const FLASH_GOOGLE = {
  conectado: 'Google Calendar vinculado. Los eventos de tu agenda se están copiando al calendario "Mi Estudio".',
  cancelado: 'Cancelaste la vinculación con Google Calendar.',
  error: 'No se pudo vincular Google Calendar. Probá de nuevo.',
};

export default function Configuracion() {
  const [form, setForm] = useState({ nombre_estudio: '', nombre_usuario: '', email: '', telefono: '', domicilio: '' });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Perfil del usuario actual
  const [perfil, setPerfil] = useState({ nombre: '', email: '', whatsapp: '' });
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);

  // Vinculo con Google Calendar
  const [gcal, setGcal] = useState({ cargando: true, connected: false, configured: true, lastSyncAt: null, lastError: null });
  const [gcalBusy, setGcalBusy] = useState(false);
  const [gcalMsg, setGcalMsg] = useState('');

  useEffect(() => { fetchConfig(); fetchPerfil(); fetchGoogleCalendar(); }, []);

  async function fetchGoogleCalendar() {
    // Mensaje de vuelta del flujo de autorizacion (lo deja AppShell).
    const flash = sessionStorage.getItem('gcal_flash');
    if (flash) {
      sessionStorage.removeItem('gcal_flash');
      setGcalMsg(FLASH_GOOGLE[flash] || '');
    }
    try {
      const st = await llamarGoogleCalendar('status');
      setGcal({ cargando: false, ...st });
    } catch {
      setGcal(g => ({ ...g, cargando: false }));
    }
  }

  async function vincularGoogle() {
    setGcalBusy(true); setGcalMsg('');
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { url } = await llamarGoogleCalendar('connect', { tz });
      window.location.href = url;
    } catch (e) {
      setGcalMsg(e.message);
      setGcalBusy(false);
    }
  }

  async function sincronizarGoogle() {
    setGcalBusy(true); setGcalMsg('');
    try {
      const r = await llamarGoogleCalendar('sync');
      if (r.revoked) {
        setGcal(g => ({ ...g, connected: false }));
        avisarEstadoGoogleCalendar(false);
        setGcalMsg('Google revocó el acceso. Volvé a vincular.');
      } else {
        const partes = [];
        if (r.creados) partes.push(`${r.creados} nuevos`);
        if (r.actualizados) partes.push(`${r.actualizados} actualizados`);
        if (r.borrados) partes.push(`${r.borrados} borrados`);
        setGcalMsg(
          (partes.length ? `Sincronizado: ${partes.join(', ')}.` : 'Todo está al día.') +
          (r.parcial ? ' Quedaron eventos pendientes, volvé a sincronizar.' : '') +
          (r.error ? ` Aviso: ${r.error}` : '')
        );
        fetchGoogleCalendar();
      }
    } catch (e) {
      setGcalMsg(e.message);
    }
    setGcalBusy(false);
  }

  async function desvincularGoogle() {
    if (!window.confirm('Se va a borrar el calendario "Mi Estudio" de tu Google Calendar y se revocará el acceso. ¿Continuar?')) return;
    setGcalBusy(true); setGcalMsg('');
    try {
      await llamarGoogleCalendar('disconnect');
      setGcal(g => ({ ...g, connected: false, lastSyncAt: null, lastError: null }));
      avisarEstadoGoogleCalendar(false);
      setGcalMsg('Google Calendar desvinculado.');
    } catch (e) {
      setGcalMsg(e.message);
    }
    setGcalBusy(false);
  }

  async function fetchConfig() {
    const { data } = await supabase.from('configuracion').select('*');
    if (data) {
      const config = {};
      data.forEach(row => { config[row.clave] = row.valor; });
      setForm(f => ({ ...f, ...config }));
    }
    setLoading(false);
  }

  async function fetchPerfil() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data } = await supabase.from('profiles').select('nombre, whatsapp').eq('id', session.user.id).single();
    setPerfil({ nombre: data?.nombre || '', email: session.user.email || '', whatsapp: data?.whatsapp || '' });
  }

  async function savePerfil() {
    if (!perfil.nombre.trim()) { showToast('El nombre no puede estar vacío'); return; }
    setGuardandoPerfil(true);
    const { data: { session } } = await supabase.auth.getSession();
    const whatsappGuardar = perfil.whatsapp.trim()
      ? 'whatsapp:' + perfil.whatsapp.trim().replace(/^whatsapp:/, '')
      : null;
    await supabase.from('profiles')
      .update({ nombre: perfil.nombre.trim(), whatsapp: whatsappGuardar })
      .eq('id', session.user.id);
    setGuardandoPerfil(false);
    showToast('Perfil actualizado ✓');
  }

  async function saveConfig() {
    for (const [clave, valor] of Object.entries(form)) {
      await supabase.from('configuracion').upsert({ clave, valor }, { onConflict: 'clave' });
    }
    showToast('Configuración guardada ✓');
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  if (loading) return <div className="page" style={{ color: 'var(--text4)' }}>Cargando...</div>;

  return (
    <div className="page">
      <div className="page-topbar">
        <span className="page-title">Configuración</span>
      </div>
      <div className="page-content">

        {/* MI PERFIL */}
        <div className="card" style={{ maxWidth: 580 }}>
          <div className="card-header">
            <span className="card-title">Mi perfil</span>
          </div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Nombre</label>
                <input
                  className="form-input"
                  value={perfil.nombre}
                  onChange={e => setPerfil(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Tu nombre completo"
                />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Email (no editable)</label>
                <input className="form-input" value={perfil.email} disabled style={{ opacity: 0.6 }} />
              </div>
            </div>

            {/* BOT DE WHATSAPP */}
            <div style={{ borderTop: '1px solid var(--border2)', marginTop: 20, paddingTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Bot de WhatsApp</div>
              <div style={{ fontSize: 12, color: 'var(--text4)', marginBottom: 12 }}>
                Vinculá tu número para consultar tareas, audiencias y vencimientos desde WhatsApp.
              </div>
              <div className="form-group form-full">
                <label className="form-label">Tu número de WhatsApp</label>
                <input
                  className="form-input"
                  value={perfil.whatsapp.replace(/^whatsapp:/, '')}
                  onChange={e => setPerfil(p => ({ ...p, whatsapp: e.target.value }))}
                  placeholder="+5491112345678"
                />
                <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 4 }}>
                  Formato internacional con código de país. Ej: +5491112345678
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-primary" onClick={savePerfil} disabled={guardandoPerfil}>
                {guardandoPerfil ? 'Guardando...' : 'Guardar perfil'}
              </button>
            </div>
          </div>
        </div>

        {/* GOOGLE CALENDAR */}
        <div className="card" style={{ maxWidth: 580, marginTop: 16 }}>
          <div className="card-header">
            <span className="card-title">Google Calendar</span>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 12, color: 'var(--text4)', marginBottom: 14, lineHeight: 1.6 }}>
              Copia los eventos de la agenda del estudio a un calendario "Mi Estudio" dentro de tu cuenta de Google,
              para verlos en el celular junto con el resto de tus calendarios. Es en un solo sentido: lo que
              edites en Google no vuelve a la app, y tu calendario principal no se toca.
            </div>

            {gcal.cargando ? (
              <div style={{ fontSize: 13, color: 'var(--text4)' }}>Consultando estado…</div>
            ) : !gcal.configured ? (
              <div style={{ fontSize: 13, color: 'var(--red)' }}>La integración todavía no está habilitada en el servidor.</div>
            ) : gcal.connected ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Vinculado</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text4)', marginBottom: 12 }}>
                  {gcal.lastSyncAt
                    ? `Última sincronización: ${new Date(gcal.lastSyncAt).toLocaleString('es-AR')}`
                    : 'Todavía no se sincronizó.'}
                </div>
                {gcal.lastError && (
                  <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>Último error: {gcal.lastError}</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={sincronizarGoogle} disabled={gcalBusy}>
                    {gcalBusy ? 'Sincronizando…' : 'Sincronizar ahora'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={desvincularGoogle} disabled={gcalBusy}>
                    Desvincular
                  </button>
                </div>
              </>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={vincularGoogle} disabled={gcalBusy}>
                {gcalBusy ? 'Redirigiendo…' : 'Vincular Google Calendar'}
              </button>
            )}

            {gcalMsg && (
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 12, lineHeight: 1.5 }}>{gcalMsg}</div>
            )}
          </div>
        </div>

        {/* DATOS DEL ESTUDIO */}
        <div className="card" style={{ maxWidth: 580, marginTop: 16 }}>
          <div className="card-header">
            <span className="card-title">Datos del estudio</span>
          </div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Nombre del estudio</label>
                <input className="form-input" value={form.nombre_estudio} onChange={e => setForm(f => ({ ...f, nombre_estudio: e.target.value }))} placeholder="Ej: Estudio Jurídico Biegler" />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Nombre del titular</label>
                <input className="form-input" value={form.nombre_usuario} onChange={e => setForm(f => ({ ...f, nombre_usuario: e.target.value }))} placeholder="Ej: Dr. Facundo Biegler" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="estudio@email.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} placeholder="Teléfono de contacto" />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Domicilio</label>
                <input className="form-input" value={form.domicilio} onChange={e => setForm(f => ({ ...f, domicilio: e.target.value }))} placeholder="Dirección del estudio" />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-primary" onClick={saveConfig}>Guardar cambios</button>
            </div>
          </div>
        </div>

        {/* ACERCA DEL SISTEMA */}
        <div className="card" style={{ maxWidth: 580, marginTop: 16 }}>
          <div className="card-header">
            <span className="card-title">Acerca del sistema</span>
          </div>
          <div className="card-body">
            {[
              { label: 'Sistema', value: 'Mi Estudio — Gestión Jurídica Integral' },
              { label: 'Versión', value: '1.0.0 — Beta' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '0.5px solid var(--border)' }}>
                <span style={{ fontSize: 13, color: 'var(--text4)' }}>{item.label}</span>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {toast && <div className="toast">✓ {toast}</div>}
    </div>
  );
}
